import { useMemo } from "react";
import { Breadcrumbs, Link, Stack, TablePagination, Typography } from "@mui/material";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { GenerateUsuariosListaColumns } from "./columns";
import { useUsuariosListaViewModel } from "../../viewModels/usuarios_lista_view_model";
import { UsuariosListaDao } from "../../../domain/models/usuariosLista";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { LegendIcon } from "../../../../../common/components/legend_icon";

import RefreshButton from "../../../../../common/components/refresh_button";
import PrimaryButton from "../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * UsuariosLista
 */

export const UsuariosLista = () => {
  const viewModel = useUsuariosListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateUsuariosListaColumns({
      idUsuario: viewModel.idUsuario,
      onActionEdit: viewModel.handleEditUsuarios,
      onActionDelete: viewModel.handleUsuariosDeleteOpenState,
      onActionBlock: viewModel.handleUsuariosBloqueioOpenState,
    });
  }, [
    viewModel.idUsuario,
    viewModel.handleEditUsuarios,
    viewModel.handleUsuariosDeleteOpenState,
    viewModel.handleUsuariosBloqueioOpenState,
  ]);

  return (
    <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
      <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
        <Link underline="hover" color="inherit" >
          Administração
        </Link>
        <Typography className="text-primary-green">
          Usuários
        </Typography>
      </Breadcrumbs>

      <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
        <div className="flex gap-2 items-center w-full">
          <h1 className="text-3xl font-medium text-black dark:text-white">Usuários</h1>
          <RefreshButton onClick={viewModel.handleReload} />
        </div>

        <div className="w-full justify-end flex gap-2">
          <div className="w-[180px] ">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
              onClick={viewModel.handleNewUsuarios}>
              Novo Usuário
            </PrimaryButton>
          </div>
        </div>
      </div>
      {
        viewModel.errorMessage !== undefined
          ? (
            <div className="w-full">
              <AlertError
                messageErro={viewModel.errorMessage}
                onTryAgain={viewModel.handleReload} />
            </div>
          ) : (
            <>
              <Header
                nome={viewModel.filters.nome}
                login={viewModel.filters.login}
                idsGrupoAcesso={viewModel.filters.idsGrupoAcesso}
                ddlGrupoAcesso={viewModel.ddlGrupoAcesso}
                ddlCentroCusto={viewModel.ddlCentroCusto}
                idsCentroCusto={viewModel.filters.idsCentroCusto}
                email={viewModel.filters.email}
                status={viewModel.filters.status}
                handleNomeChange={viewModel.handleNomeChange}
                handleLoginChange={viewModel.handleLoginChange}
                handleIdsGrupoAcessoChange={viewModel.handleIdsGrupoAcessoChange}
                handleIdsCentroCustoChange={viewModel.handleIdsCentroCustoChange}
                handleEmailChange={viewModel.handleEmailChange}
                handleStatusChange={viewModel.handleStatusChange}
                handleClearFilters={viewModel.handleClearFilters}

              />

              {
                viewModel.internalErrorMessage !== undefined &&
                <div className="w-full">
                  <AlertError
                    messageErro={viewModel.internalErrorMessage}
                    onTryAgain={viewModel.handleReload}
                    onClose={() => viewModel.setInternalErrorMessage(undefined)} />
                </div>
              }

              <Stack spacing={0}>
                <div className="w-full flex flex-row justify-between items-center">
                  <LegendComponent>
                    <LegendItemComponent
                      icon={<LegendIcon color='green' title={String(viewModel.nroDeUsuariosTotal?.nroUsuariosAtivos ?? 0)} />}
                      label='Usuários Ativos cadastrados' />

                    <LegendItemComponent
                      icon={<LegendIcon color='yellow' title={String(viewModel.nroDeUsuariosTotal?.nroUsuariosTotal ?? 0)} />}
                      label='Limite de usuários ativos' />
                  </LegendComponent>

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </div>

                <StyledDataGrid
                  tableName="usuarios_lista"
                  disableColumnMenu={false}
                  showToolbar={true}
                  disableRowSelectionOnClick={true}
                  getRowId={(row: UsuariosListaDao) => row.idUsuario}
                  columns={columnsDefinition}
                  loading={viewModel.loading}
                  checkboxSelection={false}
                  onRowSelectionModelChange={viewModel.handleRowSelectionModelChange}
                  rowSelectionModel={viewModel.rowSelectionModel}
                  rows={viewModel.usuariosLista}
                  getRowHeight={() => 'auto'}
                  rowCount={viewModel.usuariosLista.length}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationMode="server"
                  paginationModel={{
                    page: viewModel.pagination.page,
                    pageSize: viewModel.pagination.rowsPerPage,
                  }}
                // pinnedColumns={{
                //   left: ["__check__"],
                //   right: ['idUsuario'],
                // }}
                />

                <TablePagination
                  component="div"
                  count={viewModel.pagination.totalItems}
                  page={viewModel.pagination.page - 1}
                  onPageChange={(_, p) => viewModel.handlePageChange(p)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  rowsPerPage={viewModel.pagination.rowsPerPage}
                  onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

              </Stack>
            </>
          )
      }

      <DeleteDialog
        open={viewModel.openDeleteModal}
        saving={false}
        onClose={() => viewModel.handleUsuariosDeleteOpenState(null, false)}
        title={"Você realmente deseja excluir o usuário?"}
        onDelete={viewModel.handleConfirmarDeletarUsuarios} />


      <DeleteDialog
        open={viewModel.openBloqueioModal}
        saving={false}
        windowTitle="Bloquear Usuário"
        buttonTitle="Bloquear"
        onClose={() => viewModel.handleUsuariosBloqueioOpenState(null, false)}
        title={"Você realmente deseja Bloquear o usuário?"}
        onDelete={viewModel.handleConfirmarBloqueioUsuarios} />

    </div>
  );
}